import Big from "big.js";
import { ZERO } from "./bigUtil.ts";
import { Cost, CostType, DisbursementType } from "./CaseConfiguration.ts";
import type { CostsByType } from "./CaseModelUtility.ts";

/*
 * VAT on legal costs. A party registered for VAT reclaims the VAT it is
 * charged: VAT costs it nothing, it has no rate, and VAT is left out of all of
 * its figures — it cannot recover any from an opponent either. A party that
 * cannot reclaim it (an individual, a business not registered for VAT) has a
 * rate: it pays VAT on its costs and recovers that VAT, with the costs, from
 * an opponent ordered to pay them.
 */

/** The standard rate of UK VAT, the rate a party is given first. */
export const STANDARD_VAT_RATE = 0.2;

/**
 * Court fees carry no VAT. Every other cost does: law firm fees, barrister
 * fees, hired experts such as investigators or analysts, and other
 * disbursements.
 */
export function carriesVat(type: CostType): boolean {
  return type !== "courtFee";
}

/**
 * The VAT a party with `vatRate` pays on an amount of costs of `type`: none
 * without a rate, and none on court fees.
 */
export function vatOn(
  amount: number,
  type: CostType,
  vatRate: number | undefined,
): Big {
  return vatRate === undefined || !carriesVat(type)
    ? ZERO
    : new Big(amount).mul(vatRate);
}

/**
 * The VAT contained in an amount a party with `vatRate` paid, or recovers, on
 * costs of `type` with their VAT: rate / (1 + rate) of it, none on court fees.
 * The ledger splits such amounts — enforcement spend, recoveries — into the
 * costs and their VAT.
 */
export function vatIncludedIn(
  amount: Big.BigSource,
  type: CostType,
  vatRate: number | undefined,
): Big {
  return vatRate === undefined || !carriesVat(type)
    ? ZERO
    : new Big(amount).mul(vatRate).div(new Big(1).add(vatRate));
}

/** The VAT a party with `vatRate` pays on costs summed up by type. */
export function vatOnCosts(
  { lawFirmFees, disbursements }: CostsByType,
  vatRate: number | undefined,
): Big {
  return (Object.entries(disbursements) as [DisbursementType, Big][]).reduce(
    (sum, [type, amount]) => sum.add(vatOn(amount.toNumber(), type, vatRate)),
    vatOn(lawFirmFees.toNumber(), "lawFirmFee", vatRate),
  );
}

/**
 * Costs as a party with `vatRate` pays them: each with the VAT it carries
 * added to its amount, and to its parts towards single opponents. This is
 * what the party recovers a share of from a losing opponent, spends on
 * enforcing, and has spent by a stage.
 */
export function paidWithVat(
  costs: Cost[],
  vatRate: number | undefined,
): Cost[] {
  if (vatRate === undefined) return costs;
  return costs.map((cost) => {
    const withVat = (amount: number) =>
      new Big(amount).add(vatOn(amount, cost.type, vatRate)).toNumber();
    return {
      ...cost,
      amount: withVat(cost.amount),
      ...(cost.towards && {
        towards: Object.fromEntries(
          Object.entries(cost.towards).map(([id, part]) => [id, withVat(part)]),
        ),
      }),
    };
  });
}
